import { useCallback, useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { createRingtone } from "@/lib/sound";
import { AUDIO_CONSTRAINTS, tuneOpus, monitorStats } from "@/lib/webrtc";

// Fallback if the backend TURN endpoint is unreachable. STUN alone only works
// when both peers are on the same network / simple NAT.
const FALLBACK_ICE = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

// WebRTC 1:1 audio call over the realtime signaling channel.
// callState: "idle" | "calling" | "incoming" | "connected"
// netState (while connected): "connecting" | "good" | "poor" | "reconnecting"
export function useAudioCall({ send, conversationId, selfName }) {
  const [callState, setCallState] = useState("idle");
  const [peerName, setPeerName] = useState("");
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [netState, setNetState] = useState("connecting");
  const [level, setLevel] = useState(0);

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pendingOfferRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const timerRef = useRef(null);
  const iceServersRef = useRef(FALLBACK_ICE);
  const callStateRef = useRef("idle");
  const currentCallConvRef = useRef(null);
  const callingTimeoutRef = useRef(null);
  const roleRef = useRef(null); // "caller" | "callee"
  const statsStopRef = useRef(null);
  const restartTimerRef = useRef(null);
  const restartsRef = useRef(0);

  const ringtoneRef = useRef(null);
  if (!ringtoneRef.current) ringtoneRef.current = createRingtone();
  useEffect(() => {
    const rt = ringtoneRef.current;
    if (callState === "incoming" || callState === "calling") rt.start();
    else rt.stop();
    return () => rt.stop();
  }, [callState]);

  const setState = useCallback((s) => {
    callStateRef.current = s;
    setCallState(s);
  }, []);

  const cleanup = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(callingTimeoutRef.current);
    clearTimeout(restartTimerRef.current);
    if (statsStopRef.current) { statsStopRef.current(); statsStopRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.oniceconnectionstatechange = null;
      try { pcRef.current.close(); } catch {}
      pcRef.current = null;
    }
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    pendingOfferRef.current = null;
    pendingCandidatesRef.current = [];
    currentCallConvRef.current = null;
    roleRef.current = null;
    restartsRef.current = 0;
    setMuted(false);
    setSeconds(0);
    setLevel(0);
    setNetState("connecting");
  }, []);

  useEffect(() => {
    const el = document.createElement("audio");
    el.autoplay = true;
    el.setAttribute("playsinline", "true");
    el.style.display = "none";
    document.body.appendChild(el);
    remoteAudioRef.current = el;

    let alive = true;
    axios
      .get(`${API}/turn-credentials`)
      .then(({ data }) => {
        if (alive && Array.isArray(data?.iceServers) && data.iceServers.length) {
          iceServersRef.current = data.iceServers;
        }
      })
      .catch(() => {});

    return () => {
      alive = false;
      cleanup();
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTimer = () => {
    setSeconds(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const startStats = useCallback(() => {
    if (statsStopRef.current || !pcRef.current) return;
    statsStopRef.current = monitorStats(pcRef.current, ({ level: lv, quality }) => {
      setLevel(lv);
      setNetState((s) => (s === "reconnecting" ? s : quality === "poor" ? "poor" : "good"));
    });
  }, []);

  // Caller-initiated ICE restart to recover a dropped/failed connection
  // (e.g. WiFi→cellular). Guarded and capped so it never loops.
  const iceRestart = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || roleRef.current !== "caller" || restartsRef.current >= 2) return;
    restartsRef.current += 1;
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      offer.sdp = tuneOpus(offer.sdp);
      await pc.setLocalDescription(offer);
      send({ type: "webrtc", conversation_id: conversationId, kind: "offer", sdp: pc.localDescription });
    } catch {}
  }, [conversationId, send]);

  const attachConnHandlers = useCallback((pc) => {
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === "connected" || st === "completed") {
        clearTimeout(restartTimerRef.current);
        restartsRef.current = 0;
        setNetState("good");
      } else if (st === "disconnected") {
        setNetState("reconnecting");
        clearTimeout(restartTimerRef.current);
        // Give ICE a moment to self-heal; if not, the caller restarts.
        restartTimerRef.current = setTimeout(() => {
          if (pcRef.current && ["disconnected", "failed"].includes(pcRef.current.iceConnectionState)) iceRestart();
        }, 3000);
      } else if (st === "failed") {
        setNetState("reconnecting");
        iceRestart();
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") setNetState((s) => (s === "reconnecting" ? "good" : s));
      if (pc.connectionState === "closed" && callStateRef.current !== "idle") {
        cleanup();
        setState("idle");
      }
    };
  }, [cleanup, setState, iceRestart]);

  const buildPeer = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: false });
    localStreamRef.current = stream;
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        send({ type: "webrtc", conversation_id: conversationId, kind: "candidate", candidate: e.candidate });
      }
    };
    pc.ontrack = (e) => {
      const el = remoteAudioRef.current;
      if (el) {
        el.srcObject = e.streams[0];
        const p = el.play?.();
        if (p && p.catch) p.catch(() => {});
      }
    };
    attachConnHandlers(pc);
    pcRef.current = pc;
    return pc;
  }, [conversationId, send, attachConnHandlers]);

  const startCall = useCallback(async () => {
    try {
      roleRef.current = "caller";
      const pc = await buildPeer();
      const offer = await pc.createOffer();
      offer.sdp = tuneOpus(offer.sdp);
      await pc.setLocalDescription(offer);
      currentCallConvRef.current = conversationId;
      setNetState("connecting");
      setState("calling");
      setPeerName("");
      send({ type: "call-request", conversation_id: conversationId, offer: pc.localDescription, caller: selfName });
      clearTimeout(callingTimeoutRef.current);
      callingTimeoutRef.current = setTimeout(() => {
        if (callStateRef.current === "calling") {
          try { send({ type: "call-end", conversation_id: conversationId }); } catch {}
          cleanup();
          setState("idle");
        }
      }, 45000);
    } catch (e) {
      cleanup();
      setState("idle");
      toast.error("Microphone access is required to start a call.");
    }
  }, [buildPeer, conversationId, selfName, send, cleanup, setState]);

  const acceptCall = useCallback(async () => {
    try {
      roleRef.current = "callee";
      const pc = await buildPeer();
      const offer = pendingOfferRef.current;
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      for (const c of pendingCandidatesRef.current) {
        try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
      }
      pendingCandidatesRef.current = [];
      const answer = await pc.createAnswer();
      answer.sdp = tuneOpus(answer.sdp);
      await pc.setLocalDescription(answer);
      send({ type: "call-accept", conversation_id: conversationId, answer: pc.localDescription });
      setNetState("connecting");
      setState("connected");
      startTimer();
      startStats();
    } catch (e) {
      cleanup();
      setState("idle");
      send({ type: "call-reject", conversation_id: conversationId });
      toast.error("Microphone access is required to accept a call.");
    }
  }, [buildPeer, conversationId, send, cleanup, setState, startStats]);

  const rejectCall = useCallback(() => {
    send({ type: "call-reject", conversation_id: conversationId });
    cleanup();
    setState("idle");
  }, [conversationId, send, cleanup, setState]);

  const endCall = useCallback((notify = true) => {
    if (notify) send({ type: "call-end", conversation_id: conversationId });
    cleanup();
    setState("idle");
  }, [conversationId, send, cleanup, setState]);

  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const track = localStreamRef.current.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setMuted(!track.enabled);
    }
  }, []);

  const processSignal = useCallback(async (data) => {
    if (data.conversation_id !== conversationId) return;
    if (data.type === "call-request") {
      if (callStateRef.current !== "idle") {
        if (currentCallConvRef.current && currentCallConvRef.current !== data.conversation_id) {
          send({ type: "call-reject", conversation_id: data.conversation_id });
        }
        return;
      }
      currentCallConvRef.current = data.conversation_id;
      pendingOfferRef.current = data.offer;
      setPeerName(data.caller || data.from_name || "Caller");
      setState("incoming");
    } else if (data.type === "call-accept") {
      if (pcRef.current && data.answer) {
        try {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
          for (const c of pendingCandidatesRef.current) {
            try { await pcRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch {}
          }
          pendingCandidatesRef.current = [];
          setState("connected");
          startTimer();
          startStats();
        } catch {}
      }
    } else if (data.type === "call-reject" || data.type === "call-end") {
      cleanup();
      setState("idle");
    } else if (data.type === "webrtc" && data.kind === "candidate") {
      const pc = pcRef.current;
      if (pc && pc.remoteDescription) {
        try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch {}
      } else {
        pendingCandidatesRef.current.push(data.candidate);
      }
    } else if (data.type === "webrtc" && data.kind === "offer") {
      // ICE-restart renegotiation from the caller — callee answers.
      const pc = pcRef.current;
      if (pc && data.sdp) {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          const answer = await pc.createAnswer();
          answer.sdp = tuneOpus(answer.sdp);
          await pc.setLocalDescription(answer);
          send({ type: "webrtc", conversation_id: conversationId, kind: "answer", sdp: pc.localDescription });
        } catch {}
      }
    } else if (data.type === "webrtc" && data.kind === "answer") {
      const pc = pcRef.current;
      if (pc && data.sdp) {
        try { await pc.setRemoteDescription(new RTCSessionDescription(data.sdp)); } catch {}
      }
    }
  }, [conversationId, cleanup, send, setState, startStats]);

  return {
    callState, peerName, muted, seconds, netState, level,
    startCall, acceptCall, rejectCall, endCall, toggleMute, processSignal,
  };
}
